import { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useAppDispatch } from 'store';
import { Button } from 'components/elements/button';
import { InputText } from 'components/form/input/text';
import { Loading } from 'components/feedback/loading';
import { openFlashMessage } from 'components/utils/flashMessages';
import {
  validateMFACodeFormat,
  sanitizeMFAInput,
  formatSecretForDisplay,
  MFA_CODE_LENGTH,
} from 'utils/mfa';
import { KEYS } from 'components/utils/keys';

import { setupMFA, verifyMFA } from './actions';
import { FlashDefinition } from './utils/flashDefinition';
import i18n from './utils/i18n';
import * as styled from './styles/setup';
import { BackupCodes } from './BackupCodes';

/* eslint-disable camelcase */
type MFASetupData = {
  qr_code: string;
  secret: string;
  formatted_secret: string;
  backup_codes?: string[];
};

type MFASetupProps = {
  onClose?: VoidCallback;
  onSuccess?: VoidCallback;
  onBackupCodes?: (codes: string[]) => void;
  embedded?: boolean;
};

export const MFASetup = ({
  onClose,
  onSuccess,
  onBackupCodes,
  embedded = false,
}: MFASetupProps) => {
  const intl = useIntl();
  const Wrap = embedded
    ? ({ children }: { children: React.ReactNode }) => <>{children}</>
    : styled.Wrapper;
  const dispatch = useAppDispatch();
  const [setupData, setSetupData] = useState<MFASetupData | null>(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showManualCode, setShowManualCode] = useState(false);

  const handleVerify = () => {
    const validation = validateMFACodeFormat(verificationCode);
    if (!validation.isValid) {
      openFlashMessage(
        validation.errorKey === 'required'
          ? FlashDefinition.codeRequired
          : FlashDefinition.codeInvalid
      );
      return;
    }
    setVerifying(true);
    dispatch(verifyMFA(verificationCode, setupData?.secret))
      .then((response) => {
        const codes = response?.backup_codes || setupData?.backup_codes || [];
        if (codes.length > 0) {
          if (onBackupCodes) {
            onBackupCodes(codes);
          } else {
            setSetupData((prev) =>
              prev ? { ...prev, backup_codes: codes } : prev
            );
            setShowBackupCodes(true);
          }
        }
      })
      .catch(() => {})
      .then(() => setVerifying(false));
  };

  const handleNextStep = () => setCurrentStep(2);

  const handlePreviousStep = () => {
    setCurrentStep(1);
    setVerificationCode('');
  };

  const handleBackupCodesClose = () => {
    setShowBackupCodes(false);
    if (onSuccess) {
      onSuccess();
    } else if (onClose) {
      onClose();
    }
  };

  const handleSubmit = (event: React.KeyboardEvent) => {
    if (event.nativeEvent.keyCode === KEYS.ENTER) {
      handleVerify();
    }
  };

  useEffect(() => {
    setLoading(true);
    dispatch(setupMFA())
      .then((response) => setSetupData(response))
      .catch(() => {})
      .then(() => setLoading(false));
  }, [dispatch]);

  if (loading && !setupData?.qr_code && !setupData?.secret) {
    return (
      <Wrap>
        <styled.LoadingContainer>
          <Loading />
        </styled.LoadingContainer>
      </Wrap>
    );
  }

  if (showBackupCodes) {
    return (
      <BackupCodes
        codes={setupData?.backup_codes || []}
        onClose={handleBackupCodesClose}
        showWarning
        isOpen
        variant="setup"
      />
    );
  }

  return (
    <Wrap>
      {!embedded && (
        <styled.MFATitle>
          <FormattedMessage {...i18n.setupTitle} />
        </styled.MFATitle>
      )}

      {currentStep === 1 ? (
        <>
          <styled.Section>
            {!embedded && (
              <styled.Instructions>
                <FormattedMessage {...i18n.setupInstructions} />
              </styled.Instructions>
            )}

            {setupData?.qr_code && (
              <styled.QRContainer>
                <styled.QRImage src={setupData?.qr_code} alt="MFA QR Code" />
              </styled.QRContainer>
            )}

            {!showManualCode && (
              <styled.ToggleButton
                type="blank"
                onClick={() => setShowManualCode(true)}
              >
                <FormattedMessage {...i18n.cantScanImage} />
              </styled.ToggleButton>
            )}

            {showManualCode && setupData?.secret && (
              <>
                <styled.Instructions>
                  <FormattedMessage {...i18n.manualEntryLabel} />
                </styled.Instructions>
                <styled.SecretCode>
                  {formatSecretForDisplay(setupData?.secret || '')}
                  <styled.CopyButton
                    type="blank"
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(setupData?.secret || '');
                      openFlashMessage(FlashDefinition.secretKeyCopied);
                    }}
                  >
                    <FormattedMessage {...i18n.copyButton} />
                  </styled.CopyButton>
                </styled.SecretCode>
              </>
            )}
          </styled.Section>

          <styled.ExtendedButtonGroup alignEnd={!onClose}>
            {onClose && (
              <Button
                size="large"
                color="mono"
                type="outline"
                onClick={onClose}
              >
                <FormattedMessage {...i18n.cancelButton} />
              </Button>
            )}
            <Button size="large" color="primary" onClick={handleNextStep}>
              <FormattedMessage {...i18n.nextButton} />
            </Button>
          </styled.ExtendedButtonGroup>
        </>
      ) : (
        <>
          <styled.Section>
            <styled.Instructions>
              <FormattedMessage {...i18n.verifyInstructions} />
            </styled.Instructions>
            <InputText
              name="verificationCode"
              placeholder={intl.formatMessage(i18n.codePlaceholder)}
              value={verificationCode}
              onKeyDown={handleSubmit}
              onChange={(value: string) =>
                setVerificationCode(sanitizeMFAInput(value, false))
              }
              maxlength={MFA_CODE_LENGTH}
              pattern="[0-9]*"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
            />
          </styled.Section>

          <styled.ExtendedButtonGroup>
            <Button
              size="large"
              color="mono"
              type="outline"
              onClick={handlePreviousStep}
            >
              <FormattedMessage {...i18n.backButton} />
            </Button>
            <Button
              size="large"
              color="primary"
              onClick={handleVerify}
              loading={verifying}
              disabled={
                verifying || verificationCode.length !== MFA_CODE_LENGTH
              }
            >
              <FormattedMessage {...i18n.verifyButton} />
            </Button>
          </styled.ExtendedButtonGroup>
        </>
      )}
    </Wrap>
  );
};
